import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Building2, Music, Users, Calendar, DollarSign, MessageSquare, LogOut, CheckSquare, CalendarDays, UserCog, Menu, X, Contact, Settings, ShieldCheck, Check, ChevronDown, History, ScanLine, Wallet, Link2, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { getActiveStudio, getEffectivePermissions, isAdmin as isAdminUser } from "@/lib/api";
import { useBrand } from "@/lib/brand";

const BottomNav = ({ user, onLogout }) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerStudios, setDrawerStudios] = useState([]);
  const [drawerStudioMenuOpen, setDrawerStudioMenuOpen] = useState(false);
  const activeStudio = getActiveStudio();

  // Lazy-load studios when drawer opens
  useEffect(() => {
    if (drawerOpen && drawerStudios.length === 0) {
      api.get("/studios").then((r) => setDrawerStudios(r.data?.studios || [])).catch(() => {});
    }
  }, [drawerOpen, drawerStudios.length]);

  const switchStudio = (s) => {
    localStorage.setItem("active_studio_id", s.studio_id);
    localStorage.setItem("active_studio", JSON.stringify(s));
    if (s?.branding?.primary_color) {
      document.documentElement.style.setProperty("--brand-primary", s.branding.primary_color);
    }
    window.location.reload();
  };

  const primaryNavItemsAll = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/calendar", icon: CalendarDays, label: "Calendar" },
    { path: "/attendance", icon: CheckSquare, label: "Attendance" },
    { path: "/payments", icon: DollarSign, label: "Payments" },
  ];

  // Phase-2: filter primary mobile nav by instructor permissions (admins keep all).
  // getEffectivePermissions() returns FULL_PERMS for admins so the filter never blocks them.
  const _isAdminPrimary = isAdminUser(user) || user?.is_super_admin;
  const _perms = getEffectivePermissions(user);
  const primaryNavItems = _isAdminPrimary
    ? primaryNavItemsAll
    : primaryNavItemsAll.filter((item) => {
        if (item.path === "/calendar" && _perms.calendar_access === "none") return false;
        if (item.path === "/attendance" && _perms.attendance_access !== "enabled") return false;
        if (item.path === "/payments" && _perms.payment_visibility !== "enabled") return false;
        return true;
      });

  // Order: most-used first (frequency-ranked). Dashboard + Calendar excluded — already in bottom ribbon.
  const allNavItems = [
    { path: "/students", icon: Users, label: "Students" },
    { path: "/customers", icon: Contact, label: "Customers" },
    { path: "/classes", icon: Calendar, label: "Classes" },
    { path: "/pickup", icon: ScanLine, label: "Pickup" },
    { path: "/announcements", icon: MessageSquare, label: "Announcements" },
    { path: "/instructors", icon: UserCog, label: "Instructors" },
    { path: "/payroll", icon: Wallet, label: "Payroll" },
    { path: "/ledger", icon: BookOpen, label: "Ledger" },
    { path: "/audit-log", icon: History, label: "Audit Log" },
    { path: "/dance-styles", icon: Music, label: "Dance Styles" },
    { path: "/studios", icon: Building2, label: "Studios" },
    { path: "/studio-settings", icon: Settings, label: "Studio Settings" },
    { path: "/users", icon: ShieldCheck, label: "User Access" },
    { path: "/join-links", icon: Link2, label: "Join Links" },
  ];

  return (
    <>
      {/* Bottom Nav - Mobile Only */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-lg border-t border-slate-200 flex justify-around items-center z-50 md:hidden">
        {primaryNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="flex flex-col items-center justify-center flex-1 h-full relative"
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-pink-500"
                />
              )}
              <Icon className={`w-5 h-5 ${isActive ? "text-primary" : "text-slate-400"}`} />
              <span className={`text-[10px] mt-0.5 ${isActive ? "text-primary font-medium" : "text-slate-400"}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
        {/* More / Menu button */}
        <button
          data-testid="nav-more-menu"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-col items-center justify-center flex-1 h-full"
        >
          <Menu className="w-5 h-5 text-slate-400" />
          <span className="text-[10px] mt-0.5 text-slate-400">More</span>
        </button>
      </nav>

      {/* Drawer Overlay */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[60] md:hidden"
              onClick={() => setDrawerOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-72 bg-white shadow-2xl z-[70] md:hidden flex flex-col"
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold font-heading brand-text-gradient">Menu</h2>
                <button
                  data-testid="close-drawer"
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 rounded-full hover:bg-slate-100"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Active studio + switcher */}
              {activeStudio && (
                <div className="px-3 pt-3">
                  <button
                    data-testid="drawer-studio-toggle"
                    onClick={() => setDrawerStudioMenuOpen((v) => !v)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--brand-primary) 8%, white)",
                      borderColor: "color-mix(in srgb, var(--brand-primary) 25%, white)",
                    }}
                  >
                    <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Active Studio</div>
                      <div className="text-sm font-semibold truncate" style={{ color: "var(--brand-primary)" }}>{activeStudio.name}</div>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${drawerStudioMenuOpen ? "rotate-180" : ""}`} style={{ color: "var(--brand-primary)" }} />
                  </button>

                  <AnimatePresence>
                    {drawerStudioMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                        data-testid="drawer-studio-switcher"
                      >
                        <div className="mt-1 mb-1 py-1 px-1 max-h-56 overflow-y-auto">
                          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">Switch to</div>
                          {drawerStudios.length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-500">Loading studios…</div>
                          )}
                          {drawerStudios.map((s) => {
                            const isCurrent = s.studio_id === activeStudio.studio_id;
                            return (
                              <button
                                key={s.studio_id}
                                data-testid={`drawer-switch-studio-${s.studio_id}`}
                                onClick={() => switchStudio(s)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-50 transition text-left ${isCurrent ? "bg-slate-50" : ""}`}
                              >
                                <Building2 className={`w-4 h-4 flex-shrink-0 ${isCurrent ? "" : "text-slate-400"}`} style={isCurrent ? { color: "var(--brand-primary)" } : undefined} />
                                <span className={`flex-1 truncate ${isCurrent ? "font-medium" : "text-slate-700"}`} style={isCurrent ? { color: "var(--brand-primary)" } : undefined}>{s.name}</span>
                                {isCurrent && <Check className="w-4 h-4 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {allNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`drawer-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setDrawerOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-violet-50 to-pink-50 text-primary font-medium"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
              <div className="p-3 border-t border-slate-100">
                <button
                  data-testid="drawer-logout"
                  onClick={() => { setDrawerOpen(false); onLogout?.(); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 w-full transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Mobile-only sticky top bar: shows display name + active studio chip with switcher.
// Hidden on desktop (md:hidden).
export const MobileTopBar = () => {
  const { display_name } = useBrand();
  const activeStudio = getActiveStudio();
  const [open, setOpen] = useState(false);
  const [studios, setStudios] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (open && studios.length === 0) {
      api.get("/studios").then((r) => setStudios(r.data?.studios || [])).catch(() => {});
    }
  }, [open, studios.length]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const switchStudio = (s) => {
    localStorage.setItem("active_studio_id", s.studio_id);
    localStorage.setItem("active_studio", JSON.stringify(s));
    if (s?.branding?.primary_color) {
      document.documentElement.style.setProperty("--brand-primary", s.branding.primary_color);
    }
    window.location.reload();
  };

  return (
    <header
      data-testid="mobile-top-bar"
      className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 px-4 h-14 flex items-center justify-between"
    >
      <h1 className="text-lg font-bold font-heading brand-text-gradient truncate" data-testid="mobile-brand-title">
        {display_name}
      </h1>
      {activeStudio && (
        <div ref={ref} className="relative">
          <button
            data-testid="mobile-active-studio-chip"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition cursor-pointer max-w-[55vw]"
            style={{
              backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, white)",
              borderColor: "color-mix(in srgb, var(--brand-primary) 30%, white)",
              borderWidth: 1,
            }}
          >
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />
            <span className="text-xs font-medium truncate" style={{ color: "var(--brand-primary)" }}>{activeStudio.name}</span>
            <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "var(--brand-primary)" }} />
          </button>
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.97 }}
                transition={{ duration: 0.12 }}
                data-testid="mobile-studio-switcher-menu"
                className="absolute right-0 top-full mt-2 w-60 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1 max-h-72 overflow-y-auto"
              >
                <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Switch Studio</div>
                {studios.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
                )}
                {studios.map((s) => {
                  const isCurrent = s.studio_id === activeStudio.studio_id;
                  return (
                    <button
                      key={s.studio_id}
                      data-testid={`mobile-switch-studio-${s.studio_id}`}
                      onClick={() => switchStudio(s)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition text-left ${isCurrent ? "bg-slate-50" : ""}`}
                    >
                      <Building2 className={`w-4 h-4 flex-shrink-0 ${isCurrent ? "" : "text-slate-400"}`} style={isCurrent ? { color: "var(--brand-primary)" } : undefined} />
                      <span className={`flex-1 truncate ${isCurrent ? "font-medium" : "text-slate-700"}`} style={isCurrent ? { color: "var(--brand-primary)" } : undefined}>{s.name}</span>
                      {isCurrent && <Check className="w-4 h-4 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </header>
  );
};

export const Sidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const { display_name } = useBrand();
  const [studioMenuOpen, setStudioMenuOpen] = useState(false);
  const [availableStudios, setAvailableStudios] = useState([]);
  const chipRef = useRef(null);

  const adminNavItems = [
    { path: "/", icon: Home, label: "Dashboard" },
    { path: "/calendar", icon: CalendarDays, label: "Calendar" },
    { path: "/studios", icon: Building2, label: "Studios" },
    { path: "/studio-settings", icon: Settings, label: "Studio Settings" },
    { path: "/users", icon: ShieldCheck, label: "User Access" },
    { path: "/dance-styles", icon: Music, label: "Dance Styles" },
    { path: "/classes", icon: Calendar, label: "Classes" },
    { path: "/students", icon: Users, label: "Students" },
    { path: "/customers", icon: Contact, label: "Customers" },
    { path: "/instructors", icon: UserCog, label: "Instructors" },
    { path: "/payroll", icon: Wallet, label: "Payroll" },
    { path: "/ledger", icon: BookOpen, label: "Ledger" },
    { path: "/attendance", icon: CheckSquare, label: "Attendance" },
    { path: "/pickup", icon: ScanLine, label: "Pickup" },
    { path: "/payments", icon: DollarSign, label: "Payments" },
    { path: "/audit-log", icon: History, label: "Audit Log" },
    { path: "/announcements", icon: MessageSquare, label: "Announcements" },
    { path: "/join-links", icon: Link2, label: "Join Links" },
  ];

  const instructorNavItems = [
    { path: "/", icon: Home, label: "Dashboard" },
    { path: "/calendar", icon: CalendarDays, label: "Calendar" },
    { path: "/students", icon: Users, label: "Students" },
    { path: "/attendance", icon: CheckSquare, label: "Attendance" },
    { path: "/pickup", icon: ScanLine, label: "Pickup" },
    { path: "/payments", icon: DollarSign, label: "Payments" },
  ];

  const isAdmin = isAdminUser(user) || user?.is_super_admin;
  // Phase-2: filter instructor nav items by their permissions config
  // (Admin users get FULL_PERMS — any stale permissions object on their record is ignored.)
  const perms = getEffectivePermissions(user);
  const filteredInstructorNav = instructorNavItems.filter((item) => {
    if (item.path === "/calendar" && perms.calendar_access === "none") return false;
    if (item.path === "/attendance" && perms.attendance_access !== "enabled") return false;
    if (item.path === "/payments" && perms.payment_visibility !== "enabled") return false;
    return true;
  });
  const navItems = isAdmin ? adminNavItems : filteredInstructorNav;
  const activeStudio = getActiveStudio();

  // Lazy-load studios when chip is clicked
  const openStudioMenu = async () => {
    if (!studioMenuOpen) {
      try {
        const res = await api.get("/studios");
        setAvailableStudios(res.data?.studios || []);
      } catch (e) {
        setAvailableStudios([]);
      }
    }
    setStudioMenuOpen((v) => !v);
  };

  // Close on outside click
  useEffect(() => {
    if (!studioMenuOpen) return;
    const handler = (e) => {
      if (chipRef.current && !chipRef.current.contains(e.target)) setStudioMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [studioMenuOpen]);

  const switchStudio = (s) => {
    localStorage.setItem("active_studio_id", s.studio_id);
    localStorage.setItem("active_studio", JSON.stringify(s));
    if (s?.branding?.primary_color) {
      // Apply pre-reload so the brief flash before reload uses correct color
      document.documentElement.style.setProperty("--brand-primary", s.branding.primary_color);
    }
    // Hard reload so all in-memory queries scoped to old studio reset
    window.location.reload();
  };

  return (
    <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 flex-col">
      <div className="p-6 border-b border-slate-200 relative">
        <h1 className="text-2xl font-bold font-heading brand-text-gradient" data-testid="sidebar-title">{display_name}</h1>
        <p className="text-sm text-slate-600 mt-1">{user.name}</p>
        <p className="text-xs text-slate-400 capitalize">{user.role?.replace("_", " ")}</p>
        {activeStudio && (
          <div ref={chipRef} className="relative mt-3 inline-block">
            <button
              data-testid="active-studio-chip"
              onClick={openStudioMenu}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition cursor-pointer"
              style={{
                backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, white)",
                borderColor: "color-mix(in srgb, var(--brand-primary) 30%, white)",
                borderWidth: 1,
                color: "var(--brand-primary)",
              }}
            >
              <Building2 className="w-3.5 h-3.5" style={{ color: "var(--brand-primary)" }} />
              <span className="text-xs font-medium truncate max-w-[140px]" style={{ color: "var(--brand-primary)" }}>{activeStudio.name}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${studioMenuOpen ? "rotate-180" : ""}`} style={{ color: "var(--brand-primary)" }} />
            </button>
            <AnimatePresence>
              {studioMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.97 }}
                  transition={{ duration: 0.12 }}
                  data-testid="studio-switcher-menu"
                  className="absolute left-0 top-full mt-2 w-60 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1 max-h-72 overflow-y-auto"
                >
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Switch Studio</div>
                  {availableStudios.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500">No studios available</div>
                  )}
                  {availableStudios.map((s) => {
                    const isCurrent = s.studio_id === activeStudio.studio_id;
                    return (
                      <button
                        key={s.studio_id}
                        data-testid={`switch-studio-${s.studio_id}`}
                        onClick={() => switchStudio(s)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition text-left ${isCurrent ? "bg-violet-50/60" : ""}`}
                      >
                        <Building2 className={`w-4 h-4 flex-shrink-0 ${isCurrent ? "text-violet-600" : "text-slate-400"}`} />
                        <span className={`flex-1 truncate ${isCurrent ? "text-violet-700 font-medium" : "text-slate-700"}`}>{s.name}</span>
                        {isCurrent && <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <div className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase().replace(" ", "-")}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? "bg-slate-50 brand-text font-medium"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              style={isActive ? { backgroundColor: "color-mix(in srgb, var(--brand-primary) 8%, transparent)" } : undefined}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="p-4 border-t border-slate-200">
        <button
          data-testid="logout-button"
          onClick={onLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 w-full transition-all"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default BottomNav;
